import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { Route, Routes, useParams } from 'react-router-dom';
import { UNSUBSCRIPTION_TYPE } from '../../../../core/constants/constants';
import { EntityUIState } from '../../../../core/interfaces/entitiy-ui-state';
import { getErrorMessageFromObj } from '../../../../core/utils/request';
import RetryApiAlert from '../../../../fallback-screens/components/retry-api-alert/RetryApiAlert';
import { HdButton } from '../../../../components';
import HdIcon from '../../../../media/components/icon/HdIcon';
import useEntityLogo from '../../../../media/hooks/useEntityLogo';
import { getDataIdGenerator } from '../../../../core/utils/generateDataId';
import NotificationPreferencesAPI from '../../../notification-settings/notification-preferences/api/NotificationPreferencesAPI';
import styles from './UnsubscribeNotificationsPage.module.scss';
import { UnsubscribeNotificationShimmer } from './UnsubscribeNotificationShimmer';
import { HEVO_DARK_THEME_LOGO_SVG, HEVO_LIGHT_THEME_LOGO_SVG } from '../../../../shared/constants/brand-logo';

export function UnsubscribeNotificationsPage() {
  return (
    <Routes>
      <Route
        key='unsubscribe-notification-page'
        path='/notification-preference/unsubscribe/:unsubscriptionType/:identifier'
        element={<UnsubscribeNotificationsPageInner />}
      />
    </Routes>
  );
}

export function UnsubscribeNotificationsPageInner() {
  const [unsubscribeState, setUnsubscribeState] = useState<{
    state: EntityUIState;
    promiseError?: any;
    errorMessage?: string;
    successMessage?: string;
  }>({
    state: EntityUIState.LOADING
  });

  const { identifier, unsubscriptionType } = useParams<{
    identifier: string;
    unsubscriptionType: UNSUBSCRIPTION_TYPE;
  }>();

  const brandLogo = useEntityLogo(HEVO_LIGHT_THEME_LOGO_SVG, HEVO_DARK_THEME_LOGO_SVG);

  const toDashboard = () => {
    window.location.assign(window.location.origin + '/pipeline');
  };

  const toAlertPreferences = () => {
    window.location.assign(window.location.origin + '/alerts/preferences');
  };

  const unsubscribe = () => {
    setUnsubscribeState({
      state: EntityUIState.LOADING
    });

    NotificationPreferencesAPI.unsubscribeEmailNotifications(identifier, unsubscriptionType).then(
      res => {
        setUnsubscribeState({
          state: EntityUIState.IDLE,
          successMessage: res.success_message
        });
      },
      err => {
        if (err.status < 500) {
          setUnsubscribeState({
            state: EntityUIState.IDLE,
            errorMessage: getErrorMessageFromObj(err)
          });
        } else {
          setUnsubscribeState({
            state: EntityUIState.ERRORED,
            promiseError: err
          });
        }
      }
    );
  };

  useEffect(() => {
    unsubscribe();
  }, []);

  const dataIdGenerator = getDataIdGenerator('unsubscribe-notification');

  return (
    <div className='w-100 h-100 center-flex-col justify-center bg-default'>
      <img className={styles.brandLogo} src={brandLogo} alt='brand-logo' />

      {unsubscribeState.state === EntityUIState.IDLE ? (
        <div
          className={clsx(
            'box no-item-box m-0',
            styles.noItemBox,
            unsubscribeState.errorMessage ? 'errored' : 'success'
          )}
        >
          <div className='no-item-box-icon-container'>
            <HdIcon
              className='no-item-box-icon'
              name={unsubscribeState.errorMessage ? 'email-warning' : 'email'}
            />
          </div>

          <div className='no-item-box-title'>
            {unsubscribeState.errorMessage ? 'Already Unsubscribed!' : 'Unsubscribed Successfully!'}{' '}
            😞
          </div>

          <div className='no-item-box-desc'>
            {unsubscribeState.successMessage || unsubscribeState.errorMessage}
          </div>

          <div>
            <HdButton
              dataId={dataIdGenerator('goto-dashboard')}
              variation='outline'
              className='no-item-box-action'
              onClick={toDashboard}
            >
              Go To Dashboard
            </HdButton>

            <HdButton
              dataId={dataIdGenerator('goto-alert-preferences')}
              className='no-item-box-action ml-4'
              onClick={toAlertPreferences}
            >
              Go To Alert Preferences
            </HdButton>
          </div>
        </div>
      ) : null}

      {unsubscribeState.state === EntityUIState.LOADING ? <UnsubscribeNotificationShimmer /> : null}

      {unsubscribeState.state === EntityUIState.ERRORED ? (
        <RetryApiAlert
          classes='p-0'
          dataId={dataIdGenerator('')}
          error={unsubscribeState.promiseError}
          actionHandler={unsubscribe}
        />
      ) : null}
    </div>
  );
}
